import { useLingui } from '@lingui/react/macro';

import { RequestWithRelationsFromRequestsList } from '@/models/request';
import { isExternalWorkshopUser } from '@/models/user';
import { useCurrentSession } from '@/services/auth';

import { RequestSelection } from '../hooks/useRequestSelection';

import { CollaboratorsCell } from './components/CollaboratorsCell';
import { RequestDisplayedSteps } from './components/RequestDisplayedSteps';
import { RequestDueAtCell } from './components/RequestDueAtCell';
import { StepDueDate } from './components/StepDueDate';
import { ColumnAndCell, RequestsTab } from './RequestsTab';

export const WaitingTab = ({
  requests,
  count,
  isLoading,
  isError,
  selection,
}: {
  requests: RequestWithRelationsFromRequestsList[];
  count?: number;
  isLoading: boolean;
  isError: boolean;
  selection: RequestSelection;
}) => {
  const { t } = useLingui();
  const { currentSession } = useCurrentSession();

  const columnsAndCells: ColumnAndCell[] = [
    {
      column: {
        name: t({ id: 'requests.table.column.step.title', message: 'Step' }),
        width: '1.5fr',
      },
      cell: {
        content: (request: RequestWithRelationsFromRequestsList) => (
          <RequestDisplayedSteps request={request} />
        ),
      },
    },
    {
      column: {
        name: t({ id: 'requests.table.column.step-due-date.title', message: 'Step due date' }),
        width: '1.5fr',
        sortId: 'statusDueAt',
      },
      cell: {
        content: (request: RequestWithRelationsFromRequestsList) => (
          <StepDueDate request={request} variant="row" />
        ),
      },
    },
    {
      hide: isExternalWorkshopUser(currentSession),
      column: {
        name: t({
          id: 'requests.table.column.request-due-date.title',
          message: 'Request due date',
        }),
        width: !isExternalWorkshopUser(currentSession) ? '1.5fr' : undefined,
        sortId: 'dueAt',
      },
      cell: {
        content: (request: RequestWithRelationsFromRequestsList) => (
          <RequestDueAtCell request={request} variant="row" />
        ),
      },
    },
    {
      column: {
        name: t({ id: 'requests.table.column.collaborators.title', message: 'Collaborators' }),
        width: '104px',
      },
      cell: {
        content: (request: RequestWithRelationsFromRequestsList) => (
          <CollaboratorsCell collaborators={request.collaborators} variant="row" />
        ),
      },
    },
  ];

  return (
    <RequestsTab
      requests={requests}
      count={count}
      isLoading={isLoading}
      columnsAndCellsForTab={columnsAndCells}
      cardElementsForTab={(request: RequestWithRelationsFromRequestsList) => (
        <CardElements request={request} />
      )}
      isError={isError}
      selection={selection}
    />
  );
};

const CardElements = ({ request }: { request: RequestWithRelationsFromRequestsList }) => (
  <>
    <RequestDisplayedSteps request={request} />
    <StepDueDate request={request} variant="card" />
    <CollaboratorsCell collaborators={request.collaborators} variant="card" />
  </>
);
